import { zodResolver } from "@hookform/resolvers/zod";
import { Stack } from "@mui/material";
import { FullMooneEULA } from "Components/Auth/SignUp/FullMooneEULA";
import { PoliticallyExposedCheckbox } from "Components/Onboarding/shared/PoliticallyExposedCheckbox";
import { StepBox } from "Components/Onboarding/shared/StepBox";
import { ValuesBox } from "Components/Onboarding/shared/ValuesBox";
import { FullHeightForm } from "Components/Shared/FormStyles";
import { CheckboxInput } from "Components/Shared/Inputs/Form/CheckboxInput";
import { PhoneInput } from "Components/Shared/Inputs/Form/PhoneInput";
import { getPhoneSchema } from "Schemas/phoneSchema";
import { Resources, type TFunction, useResource } from "Translations/Resources";
import type { ReactNode } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const StepResource = Resources.Onboarding.Steps.FinalStep;

const getValidationSchema = (t: TFunction) =>
  z.object({
    phone: getPhoneSchema(t),
    isPoliticallyExposedPerson: z.boolean(),
    hasNonEUResidence: z.boolean().nullish(),
    hasNonEUNationality: z.boolean().nullish(),
  });

export type FinalStepFormModel = z.infer<
  ReturnType<typeof getValidationSchema>
>;

type Props = {
  defaultValues?: Partial<FinalStepFormModel>;
  onSubmit: (data: FinalStepFormModel) => void;
  isSubmitting?: boolean;
  // Something the submit depends on is still loading or verifying.
  isSubmitDisabled?: boolean;
  affiliateCodeField?: ReactNode;
};

export const FinalStepForm = ({
  onSubmit,
  defaultValues,
  isSubmitting,
  isSubmitDisabled,
  affiliateCodeField,
}: Props) => {
  const { t } = useResource();

  const { control, handleSubmit, watch } = useForm<FinalStepFormModel>({
    resolver: zodResolver(getValidationSchema(t)),
    defaultValues,
  });

  const isPoliticallyExposedPerson = watch("isPoliticallyExposedPerson");

  return (
    <FullHeightForm onSubmit={handleSubmit(onSubmit)}>
      <StepBox
        submitButtonLabel={t(StepResource.SubmitButton)}
        submitDisabled={isPoliticallyExposedPerson || isSubmitDisabled}
        isSubmitting={isSubmitting}
      >
        <Stack justifyContent="space-between" height="100%">
          <ValuesBox title={t(Resources.Common.Contact)} hideDivider>
            <PoliticallyExposedCheckbox
              control={control}
              name="isPoliticallyExposedPerson"
              isChecked={isPoliticallyExposedPerson}
            />
            <CheckboxInput
              label={t(StepResource.Form.HasNonEUResidence)}
              control={control}
              name="hasNonEUResidence"
            />
            <CheckboxInput
              label={t(StepResource.Form.HasNonEUNationality)}
              control={control}
              name="hasNonEUNationality"
            />
            <PhoneInput
              control={control}
              name="phone"
              label={t(Resources.Common.Phone)}
            />
          </ValuesBox>
          {affiliateCodeField && (
            <ValuesBox
              title={t(Resources.Components.Affiliate.CodeField.SectionTitle)}
            >
              {affiliateCodeField}
            </ValuesBox>
          )}
          <FullMooneEULA />
        </Stack>
      </StepBox>
    </FullHeightForm>
  );
};
